(function () {
    'use strict';

    const FOCUSABLE_SELECTOR =
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"]';

    // Same focus pattern as black-friday.js / custom.js createSliderCallback
    function applySliderA11y(slick) {
      if (!slick || !slick.$slider) return;

      const sliderEl = slick.$slider[0];
      if (!sliderEl) return;

      sliderEl.querySelectorAll('button.slick-arrow').forEach(function (arrow) {
        const disabled =
          arrow.getAttribute('aria-disabled') === 'true' || arrow.disabled;
        arrow.setAttribute('tabindex', disabled ? '-1' : '0');
      });

      sliderEl.querySelectorAll('.slick-slide').forEach(function (slide) {
        const isActive = slide.classList.contains('slick-active');
        slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach(function (focusable) {
          focusable.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });
    }

    function setPanelFocusable(panel, enabled) {
      if (!panel || enabled) return;
      panel.querySelectorAll(FOCUSABLE_SELECTOR).forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
    }

    // Before Slick inits, all products are tabbable and arrows do not exist yet
    function disablePreInitFocus(root) {
      if (!root) return;
      const slider =
        root.matches && root.matches('[data-home-tab-slider]')
          ? root
          : root.querySelector('[data-home-tab-slider]');
      if (!slider || slider.classList.contains('slick-initialized')) return;
      slider.querySelectorAll(FOCUSABLE_SELECTOR).forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
    }

    function getSliderSettings() {
      return {
        rows: 0,
        dots: false,
        arrows: true,
        accessibility: false,
        prevArrow:`<button type="button" aria-label="previous products" class="slick-arrow slick-prev" style="display: flex; align-items: center; justify-content: center; margin-top: -20px;" aria-disabled="false"><img width="19" height="19" loading="lazy" sizes="24px" src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/left-arrow.svg?v=1726055837" alt="Previous Arrow" style="cursor:pointer;"><span class="screen-reader-only">Previous</span></button>`,
        nextArrow:`<button type="button" aria-label="next products" class="slick-arrow slick-next" style="display: flex; align-items: center; justify-content: center;" aria-disabled="false"><img width="19" height="19" loading="lazy" sizes="24px" src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/left-arrow.svg?v=1726055837" alt="Next Arrow" style="cursor:pointer; transform: scaleX(-1);"><span class="screen-reader-only">Next</span></button>`,
        infinite: false,
        slidesToShow: 5,
        slidesToScroll: 5,
        mobileFirst: true,
        responsive: [
          { breakpoint: 1500, settings: { slidesToShow: 5, slidesToScroll: 5 } },
          { breakpoint: 1300, settings: { slidesToShow: 4, slidesToScroll: 4 } },
          { breakpoint: 991,  settings: { slidesToShow: 3, slidesToScroll: 3 } },
          { breakpoint: 600,  settings: { slidesToShow: 2.5, slidesToScroll: 2 } },
          { breakpoint: 0,    settings: { slidesToShow: 1.5, slidesToScroll: 1 } },
        ],
      };
    }

    function initSlider($slider) {
      if (!$slider || !$slider.length) return;

      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('setPosition');
        applySliderA11y($slider.slick('getSlick'));
        return;
      }

      disablePreInitFocus($slider[0]);

      if (window._theme && typeof window._theme.initSlickSlider === 'function') {
        window._theme.initSlickSlider(
          $slider,
          getSliderSettings(),
          applySliderA11y,
          applySliderA11y
        );
        return;
      }

      if (typeof $slider.slick === 'function') {
        $slider.on('init afterChange', function (event, slick) {
          applySliderA11y(slick);
        });
        setTimeout(function () {
          $slider.slick(getSliderSettings());
        }, 100);
      }
    }

    class HomeFeaturedTabbedSlider extends HTMLElement {
      constructor() {
        super();
        this._onTabClick = this._onTabClick.bind(this);
        this._onKeydown  = this._onKeydown.bind(this);
      }

      connectedCallback() {
        this.tabs   = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('.home-tabbed-featured__panel'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click',   this._onTabClick);
          tab.addEventListener('keydown', this._onKeydown);
        });

        this.panels.forEach((panel) => {
          if (panel.classList.contains('is-active')) {
            disablePreInitFocus(panel);
          } else {
            setPanelFocusable(panel, false);
          }
        });

        const activePanel = this.querySelector('.home-tabbed-featured__panel.is-active');
        if (activePanel) this._initSliderInPanel(activePanel);
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click',   this._onTabClick);
          tab.removeEventListener('keydown', this._onKeydown);
        });
      }

      _onTabClick(e) {
        this._switchTab(e.currentTarget);
      }

      _onKeydown(e) {
        const idx = this.tabs.indexOf(e.currentTarget);
        const last = this.tabs.length - 1;
        let next = null;

        switch (e.key) {
          case 'ArrowLeft': next = idx === 0    ? last  : idx - 1; break;
          case 'ArrowRight':next = idx === last  ? 0     : idx + 1; break;
          case 'Home':      next = 0;                               break;
          case 'End':       next = last;                            break;
        }

        if (next !== null) {
          e.preventDefault();
          this._switchTab(this.tabs[next]);
        }
      }

      _switchTab(newTab) {
        const targetId = newTab.getAttribute('aria-controls') || newTab.getAttribute('data-home-tab-target');
        if (!targetId) return;

        const newPanel = this.querySelector('#' + targetId);
        if (!newPanel) return;
  
        // Deactivate all
        this.tabs.forEach((tab) => {
          tab.classList.remove('is-active');
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('tabindex', '-1');
        });
        this.panels.forEach((panel) => {
          panel.classList.remove('is-active');
          panel.hidden = true;
          setPanelFocusable(panel, false);
        });
  
        // Activate selected
        newTab.classList.add('is-active');
        newTab.setAttribute('aria-selected', 'true');
        newTab.setAttribute('tabindex', '0');
        newTab.focus();

        newPanel.classList.add('is-active');
        newPanel.hidden = false;

        this._initSliderInPanel(newPanel);
      }

      _initSliderInPanel(panel) {
        if (!window.jQuery) return;
        const $slider = window.jQuery(panel).find('[data-home-tab-slider]');
        if ($slider.length) initSlider($slider);
      }
    }

    if (!customElements.get('home-featured-tabbed-slider')) {
      customElements.define('home-featured-tabbed-slider', HomeFeaturedTabbedSlider);
    }
  
    /* ── Global initializer hook (for external callers) ────────────────── */
  
    function initSlidersIn(root) {
      if (!window.jQuery || !root) return;
      const $sliders = window.jQuery(root).find('[data-home-tab-slider]');
      $sliders.each(function () { initSlider(window.jQuery(this)); });
    }

    window._homeFeaturedTabSlider = window._homeFeaturedTabSlider || {};
    window._homeFeaturedTabSlider.initSlidersIn = initSlidersIn;

    // This file loads in the page body before footer jQuery/Slick; boot after all defer scripts run.
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('home-featured-tabbed-slider').forEach(function (component) {
        const activePanel = component.querySelector('.home-tabbed-featured__panel.is-active');
        if (activePanel) initSlidersIn(activePanel);
      });
    });
  })();